import { currencies } from "@/data/currencies";
import { useMemo } from "react";

export function createCurrencyOption() {
  return currencies
    .map((currency) => ({
      value: currency.label,
      label: `${currency.label}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Custom hook that returns formatted currency options for select fields
 *
 * This hook formats currency data into options suitable for dropdown/select components.
 * Each option contains a value (the currency label) and a display label (currency label with symbol).
 * The options are sorted alphabetically by label.
 *
 * @returns {Array<{value: string, label: string}>} An array of currency options
 *
 * @example
 * // In a component:
 * const currencyOptions = useCurrencyOptions();
 *
 * // Use with AutocompleteLabelField
 * <AutocompleteLabelField
 *   name="currency"
 *   label="Currency"
 *   options={currencyOptions}
 *   control={form.control}
 * />
 */
export function useCurrencyOptions() {
  const currencyOptions = useMemo(
    () =>
      currencies
        .map((currency) => ({
          value: currency.label,
          label: `${currency.label}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  return currencyOptions;
}

export default useCurrencyOptions;
